import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Phone, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate('/account');
      } else {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.password, formData.phone || null);
        toast.success('Account created successfully!');
        navigate('/account');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20 flex items-center justify-center">
      <div className="container mx-auto px-4 max-w-md">
        <div className="text-center mb-8">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h1 className="text-3xl font-bold text-white">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-purple-200 mt-2">
            {isLogin 
              ? 'Sign in to access your account' 
              : 'Join the Infused by Daizy family'}
          </p>
        </div>

        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={isLogin ? "default" : "outline"}
                className={isLogin 
                  ? "flex-1 bg-yellow-500 hover:bg-yellow-600 text-purple-900" 
                  : "flex-1 border-purple-500 text-purple-200 hover:bg-purple-700"}
                onClick={() => setIsLogin(true)}
              >
                Sign In
              </Button>
              <Button
                type="button"
                variant={!isLogin ? "default" : "outline"}
                className={!isLogin 
                  ? "flex-1 bg-yellow-500 hover:bg-yellow-600 text-purple-900" 
                  : "flex-1 border-purple-500 text-purple-200 hover:bg-purple-700"}
                onClick={() => setIsLogin(false)}
              >
                Register
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-purple-100">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required={!isLogin}
                      className="pl-10 bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400"
                      data-testid="auth-name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-purple-100">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400"
                    data-testid="auth-email-input"
                  />
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-purple-100">Phone (Optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10 bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400"
                      data-testid="auth-phone-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-purple-100">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pl-10 pr-10 bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400"
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-200"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-purple-100">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required={!isLogin}
                      className="pl-10 bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400"
                      data-testid="auth-confirm-password-input"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold py-6"
                data-testid="auth-submit-btn"
              >
                {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
              </Button>
            </form>

            {isLogin && (
              <p className="text-center mt-4 text-purple-300 text-sm">
                Don't have an account?{' '}
                <button 
                  onClick={() => setIsLogin(false)}
                  className="text-yellow-400 hover:text-yellow-300 underline"
                >
                  Register here
                </button>
              </p>
            )}
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-purple-300 text-sm">
          <Link to="/products" className="text-yellow-400 hover:text-yellow-300">
            Continue shopping as guest
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Auth;
